import { ResponsiveRail } from '@/components/responsive-rail';
import { usePhone } from '@/lib/responsive';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ConfigSecretPath, ConfigSecretUpdate } from '@/api/types.gen';
import {
  CONFIG_SECRET_PATHS,
  type AdminConfigWirePut,
  type ConfigDocument,
  type ConfigValue,
  type ConfigWireNode,
  useAdminConfig,
  useRetryAdminConfig,
  useSaveAdminConfig,
  useValidateAdminConfig,
} from '@/api/config-hooks';
import { QueryErrorNote, MutationErrorLine } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { useOutsideClick } from '@/components/dropdowns';
import { ApiError } from '@/api/errors';
import { fmtStampEt } from '@/lib/datetime';
import { registerUnloadPrompt } from '@/lib/app-release';
import {
  CONFIG_NAVIGATION,
  CONFIG_SECTIONS,
  getConfigSection,
  getConfigTab,
  tabForConfigPath,
  type AdminConfigSearch,
  type ConfigSectionDefinition,
  type ConfigTabDefinition,
} from './admin-config-navigation';
import { PageActions } from '@/components/page-actions';
import { CLAUDE_MODEL_SELECT_OPTIONS } from '@/lib/claude-models';
import { LlmSpendPanel } from './admin-llm-spend';

type JsonObject = Record<string, ConfigValue>;

/// Per-field guidance, keyed by dotted path.
///
/// `humanize()` (and `FIELD_LABELS` for an acronym) is otherwise the entire labelling story, which is fine for
/// `Flush Max Rows` and badly insufficient for a switch that lets the platform
/// spend money. The reasoning behind these settings lives in Rust doc comments
/// and wrangler.toml comments that never reach the operator.
const FIELD_HELP: Record<string, string> = {
  'delivery_reporting.ttd_intraday_mode':
    'Controls the independent current-day TTD Creative GraphQL lane. Off makes no requests, Shadow validates aggregate results without publishing, and Publish writes provisional delivery through the canonical reporting tables.',
  'scheduler.ttd_intraday_delivery_cron':
    'Current-day TTD delivery schedule in New York time. Leave empty for the built-in hourly schedule at 20 minutes past the hour.',
  'edge.campaign_platform_writes_enabled':
    'Lets the campaign lane create and mutate real, spending campaigns at Meta and The Trade Desk. Off means plans are built and stored but never published.',
  'edge.creative_platform_writes_enabled':
    'Lets the creative lane mint real provider creative objects. Meta preview staging always runs and is not affected by this switch.',
  'edge.audience_platform_writes_enabled':
    'Lets the audience lane create real provider audience objects. At The Trade Desk that means a CRM data segment and the upload that fills it with hashed patron emails — a segment TTD offers no way to delete once created. Off means audiences are planned and sized locally but nothing about a patron leaves this system; at The Trade Desk it also holds back changes to what a data group or audience contains, which are made once writes resume.',
  'edge.prospect_audience_uploads_enabled':
    'Lets a lookalike audience upload raw contact details for people who are NOT customers — a third party\u2019s email, phone, name and postal code, drawn from the consumer universe rather than from anything a patron gave this venue. Separate from the audience switch above, and the two are not implied by each other: leaving this off pauses prospect uploads while ordinary audience refreshes keep running. Off is the default.',
  'edge.audience_household_expansion_enabled':
    'Expands every uploaded audience member to their whole household at upload time — everyone sharing the member’s household in the consumer universe, capped per household — roughly 2.7× more people uploaded (measured; PROVISIONING §13b). Audience sizes shown in the app stay person-grain; only the upload grows. Household members’ contact details come from the consumer universe like a prospect upload’s, so this is a prospect-grade disclosure even on a customer audience. Off is the default.',
  'edge.lookalike_recent_buyers_enabled':
    'Delivers people who bought recently to every campaign that targets a lookalike audience. They are your own customer data and are governed by the audience writes switch above; a lookalike named in the list below also needs the prospect switch. The lookalike’s prospects are still screened against your own customers. Off by default — turning it on changes who a live campaign reaches, and turning it off stops recent-buyer delivery for every lookalike.',
  'edge.lookalike_recent_buyer_days':
    'How far back "recently" reaches, in days (default 30). Counts a non-refunded ticket for any production. Zero turns recent-buyer delivery off just as surely as the switch above.',
  'edge.lookalike_recent_buyers_embed_audience_ids':
    'Lookalike audiences opted in to the recent-buyers rollout. A JSON array of SAM audience ids (not a Meta or TTD id), each listed once, e.g. ["rolling_90_days_lal"]; ["*"] on its own means every lookalike. Empty, the default, lists none, and every lookalike delivers exactly as before. A listed lookalike takes effect only while the recent-buyers switch is on, the window is above zero, and both the audience writes and prospect uploads switches are on. At Meta its custom audience must also be declared as your own data (USER_PROVIDED_ONLY), as every Meta audience SAM creates now is, or be an older one declared as both your own and partner-provided data (BOTH_USER_AND_PARTNER_PROVIDED); an older partner-only one is left as it was, and Admin → Remediations → Re-mint a Meta audience swaps it for a new one. Neither this list nor anything else ever changes how an existing audience is declared. The recent-buyers audience itself cannot be listed. Removing an id rolls that lookalike back.',
  'delivery_reporting.settled_lag_days':
    'How many days a provider may still restate ATTRIBUTION for (default 30). A conversion can attribute back to an impression up to 30 days old, so a day’s attributed sales are not final until it leaves that window. This does not hide anything: every day in the window is reported and re-reported on each run, and days still inside it are simply labelled as still moving.',
  'delivery_reporting.delivery_settled_lag_days':
    'How many days a provider may still restate DELIVERY for, on top of the day itself closing (default 0). Impressions, clicks and spend for a calendar day are settled once that day ends, so zero is the honest answer for both Meta and The Trade Desk — today shows as provisional, yesterday and earlier as final. This is deliberately separate from the attribution window above; sharing one clock marked every delivery day incomplete for a month.',
  'delivery_reporting.lookback_days':
    'How many days each run re-reads and re-publishes (default 31). This is what keeps previous days current: every run pulls the whole window again and republishes each day, so late-arriving conversions update the day the conversion happened. Must be larger than both settlement lags.',
  'edge.provider_drift_remediation_enabled':
    'Let the drift sweep repair what it finds. Detection and the drift log run either way — this only switches writing back. ON makes what SAM has saved unconditionally authoritative: an edit made in Ads Manager or the TTD console is reverted within one scan interval.',
  'edge.meta_custom_conversion_writes_enabled':
    'Lets the reconciler apply custom-conversion changes at Meta. Coverage checks stay readable either way.',
  'edge.campaign_extend_meta_enabled':
    'Offers Extend flight on live and paused Meta campaigns: a new flight, starting tomorrow at the earliest, added to the same campaign and its ad sets. Meta\u2019s lifetime budget is cumulative, so unspent budget from earlier flights carries into the new one. Needs campaign platform writes. Off by default until the sandbox measurement is in.',
  'edge.campaign_extend_ttd_enabled':
    'Offers Extend flight on live and paused The Trade Desk campaigns. The campaign\u2019s ad groups are switched off for the few minutes the new flight takes to create, then follow the flight dates again. Needs campaign platform writes. Off by default until the TTD canary measurement is in.',
  'edge.api_keys_enabled':
    'Lets people create API keys (Account \u2192 API keys) and lets those keys call SMART from scripts and Claude Code. A key acts as the person who made it, never beyond their role and the scope it was given (read; edit drafts; prepare launches and administer only while the switches below allow them): it never launches, pauses or spends. Off refuses every key\u2019s request and the creation of new ones, but revokes nothing \u2014 switching it back on restores every key that was live. Off by default.',
  'edge.mcp_enabled':
    'Lets Claude and ChatGPT reach SMART through its connector (a custom connector in claude.ai or ChatGPT; each person signs in to SMART once). The app acts as the person who signed in, never beyond their role and what they ticked when connecting (read; edit drafts; prepare launches and administer only while the switches below allow them): it never launches, pauses or spends. Off refuses the connector but revokes no one\u2019s connection. Off by default.',
  'edge.credential_prepare_enabled':
    'Lets an API key or connected app whose person ticked \u201cprepare launches\u201d create new audiences (SMART starts uploading them to Meta and The Trade Desk; nothing is spent, at most 10 a day per person) and submit creatives for review. Approving creatives and launching stay with people. Off, those credentials act as read & edit; nothing is revoked, and audiences already created keep syncing. Off by default.',
  'edge.credential_admin_enabled':
    'Lets an Admin\u2019s API key or connected app whose person ticked \u201cadminister\u201d act as an Admin: change settings (never these connector & API switches or any secret), handle alerts, run remediations and invoice actions, and manage users. Never for a production-scoped Admin, and only while \u201cCredentials may prepare launches\u201d is on too. Off, those credentials act as read, edit & prepare; nothing is revoked. Off by default.',
  'invoicing.enabled':
    'Runs the weekly billing lane: on its run weekday the previous ISO week\u2019s billable spend is frozen into an invoice held for review. Off keeps every invoicing job a silent no-op. Requires the Email section and a Xero Custom Connection.',
  'invoicing.auto_approve':
    'Skips the review hold: the daily scan approves and sends each frozen invoice without a human. Off by default \u2014 leave it off until a few weeks have been reviewed by hand.',
  'invoicing.run_weekday':
    'The weekday after a week closes (Sunday) on which it is frozen for review: 1 = Monday … 7 = Sunday, default 2 (Tuesday, once Sunday\u2019s T-1 delivery has landed). The scan runs daily and catches up, so a run day that passes while SAM Server is down is handled on the next day it is up. The invoice falls due on the Friday of the week it is raised in.',
  'invoicing.first_period':
    'The earliest ISO week (YYYY-Www, e.g. 2026-W33 for Aug 10\u201316) the catch-up scan may invoice. Required while enabled, so a fresh deployment never bills history by surprise.',
  'invoicing.catch_up_weeks':
    'How many weeks after its run weekday a closed week stays in the lane\u2019s window (default 8): still frozen by the daily scan if it was missed, still reported by the dead-man alert if it was never sent.',
  'invoicing.reference_prefix':
    'Prefix of the Xero Reference, e.g. SMART gives "SMART-2026-W37" (prefix, ISO year, week, hyphenated). The reference is what stops a retried create from raising a second invoice.',
  'email.analytics_zone_id': 'Cloudflare Zone ID for the sender domain. Enables invoice delivery verification; the Email API token also needs Analytics Read for this zone. Blank leaves delivery unverified.',
  'invoicing.email_subject_template':
    'Subject of the invoice email. Tokens: {reference} {invoice_number} {period} {total} {due_date}.',
  'invoicing.adjustments_enabled':
    'Raises an adjustment invoice (SMART-YYYY-Www-ADJ1, the next SHUBERT- number) when a sent week\u2019s settled delivery grows past what was billed. Raised on the run weekday once the week is settled, always held for a human approval (auto-approve never sends one), and it bills only the difference. A decrease is never invoiced: it is flagged for a credit note in Xero. Off by default.',
  'invoicing.adjustment_min_cents':
    'A week\u2019s net increase below this many cents keeps accumulating instead of raising an adjustment (default 2500 = $25).',
  'invoicing.adjustment_window_weeks':
    'How many weeks after its run weekday a sent week is still compared with its settled delivery and may be adjusted (default 12). Also widens the daily drift check.',
  'invoicing.adjustment_email_subject_template':
    'Subject of the adjustment email. Tokens: {reference} {invoice_number} {period} {total} {due_date} (naming the adjustment) plus {original_invoice_number} {original_reference}.',
  'invoicing.xero_client_id':
    'Client id of the Xero Custom Connection app (client credentials, one organisation). The secret is entered under Credentials.',
  'invoicing.xero_contact_id':
    'The Xero ContactID (a UUID) every invoice is raised against. Find it under Contacts in Xero or via GET /Contacts.',
  'invoicing.xero_account_code':
    'Sales account code applied to every line when no item code is set. Must be blank when an item code is set: the item\u2019s own sales account applies (MEDIA and MGMT carry 4100 on this organisation), and Xero refuses an explicit code it does not carry on every line item.',
  'invoicing.xero_item_code':
    'The Xero item Code (not its ItemID) every line is sold as, e.g. MEDIA. Sent as ItemCode on each line so the invoice reports under that product; blank sends no item.',
  'invoicing.xero_fee_item_code':
    'The Xero item Code the management-fee line is sold as, e.g. MGMT. Every campaign is billed as a MEDIA line plus a fee line; required while enabled with a non-zero fee rate.',
  'invoicing.management_fee_bps':
    'Management fee as a share of each campaign\u2019s gross, in basis points (100 basis points = 1%). Frozen onto an invoice when it is generated: changing it re-freezes invoices still in review and never moves a sent one.',
  'invoicing.xero_invoice_number_prefix':
    'Prefix of the invoice numbers this lane allocates: SHUBERT gives SHUBERT-0001, SHUBERT-0002, \u2026 (one more than the highest in use in Xero or here). Xero\u2019s own INV- sequence is not used.',
  'invoicing.xero_tax_type':
    'Tax type on every line. NONE sends tax-exclusive amounts, so the invoice total equals the sum of campaign spend.',
  'invoicing.recipients_to':
    'Who receives the invoice email and PDF. JSON array of addresses; at least one is required while enabled.',
  'invoicing.recipients_cc':
    'Copied on every invoice email, e.g. an accounts mailbox that keeps the archive. JSON array of addresses.',
  'scheduler.invoicing_scan_cron':
    'Daily invoicing scan in New York time. Leave empty for the built-in 06:30 schedule.',
  'scheduler.invoicing_actions_cron':
    'How often SAM Server picks up queued invoice actions (approve, resend, sync). Leave empty for every minute.',
  'scheduler.invoicing_payment_sync_cron':
    'Daily Xero payment-status readback in New York time. Leave empty for the built-in 06:45 schedule.',
  'edge.alert_email_to':
    'Recipients for the SAM Server heartbeat alarm, which fires when its scheduled jobs stop beating. Empty still raises the in-app alert and the navbar chip; only the email is skipped.',
  'meta.purchase_pixel_id':
    'The single purchase Pixel. Used for campaign optimization AND as the destination for box-office ticket conversions — conversions may only be sent to the Pixel the creatives track against, so this is one setting rather than two that must agree. NEW CAMPAIGNS ONLY: custom conversions are minted per Pixel, so changing this leaves live campaigns on their existing one and blocks the next publish until the assigned production has a ready conversion under the new Pixel.',
  'meta.business_id':
    'The Business Manager that owns the purchase Pixel. Managed custom conversions are created on this business, not the ad account, so the account’s 100-conversion quota does not gate them. The Meta system user must hold an Admin role on this business — Employee is refused by Meta with "MANAGE_ADS_CONVERSIONS" errors. Required before enabling the custom-conversion write switch.',
  'meta.app_id':
    'The Facebook App the access token and app secret belong to. The Meta credential check in System Health introspects the token against this app every 15 minutes and shows the observed system user, scopes, and validity — a rotated token paired with the wrong app goes red there instead of failing as cryptic Graph errors.',
  'meta.page_id':
    'Facebook Page identity for published creatives. Meta creative publishing fails closed without it.',
  'meta.instagram_user_id':
    'Page-linked Instagram identity. Required by Instagram, Story and Reels placements.',
  'ttd.base':
    'API root WITHOUT a version segment, e.g. https://api.thetradedesk.com — SAM Server appends /v3 itself.',
  'r2.bucket':
    'The file-storage bucket SAM Server keeps uploaded creatives, archived logs and invoice PDFs in. Files already stored are not moved: changing it without copying them leaves every earlier file behind in the old bucket.',
  'ttd.region':
    'Honoured by the audience upload jobs. Segments created when an audience is first synced still declare Region: US regardless — a known follow-up, so do not rely on this to redeclare PII origin yet.',
  'ttd.tracking_tag_id':
    'The primary TTD conversion tracking tag (EncorePro offline conversions). Campaign creation reports it under both cross-device models, and the conversions lane delivers against it. NEW CAMPAIGNS ONLY for the reporting columns: TTD sets them at campaign creation and has no updater, so a live campaign keeps the columns it was created with. The conversions lane picks up a change immediately. The dispatcher keeps its OWN copy of this tag — rotating this without updating that copy splits attribution.',
  'ttd.secondary_tracking_tag_id':
    'The second tag of the four-column reporting layout — the image pixel purchase tag (live value tg2lzfg). Reporting only; conversions are never delivered against it. NEW CAMPAIGNS ONLY, same as the primary tag. Empty blocks campaign publish at preflight.',
  'ttd.seed_id':
    'Kokai relevance seed stamped on every created campaign (live value cj38m0xw). NEW CAMPAIGNS ONLY — it is a create-time field and nothing reads it back. Empty blocks campaign publish at preflight.',
  'ttd.geo_segment_id':
    'Geo segment every ad group targets through its geography-inclusion bid list (live value bpywp4wtwk). NEW CAMPAIGNS ONLY — it is a create-time field and nothing reads it back. Empty blocks campaign publish at preflight.',
  'ttd.roi_goal_vcpm_cents':
    'Ad-group optimization goal as viewable CPM in cents (100 = $1.00). APPLIES TO LIVE CAMPAIGNS via the TTD reconcile lane. Zero blocks campaign publish at preflight.',
  'ttd.private_contract_bid_list_id':
    'The curated deal list every ad group must associate, CTV and Display alike (the "Gio" list, live id 606052474; the Display deals are added to the same list). Open-market bidding stays closed while it is targeted, so this list IS the inventory boundary for both channels; its membership is curated in TTD, not here. NEW CAMPAIGNS ONLY: TTD accepts this at ad-group creation and offers no way to change it afterwards, so already-published ad groups keep the list they were created with until they are republished. Empty blocks campaign publish at preflight.',
  'ttd.ctv_base_bid_cpm_cents':
    'Starting bid for every CTV ad group, as CPM in cents (100 = $1.00). APPLIES TO LIVE CAMPAIGNS: the TTD reconcile lane brings published ad groups to this value within about fifteen minutes, without interrupting delivery. Must be positive and no greater than the max; either failure blocks campaign publish at preflight. Display ad groups bid with their own pair below.',
  'ttd.ctv_max_bid_cpm_cents':
    'Bid ceiling for every CTV ad group, as CPM in cents. A ceiling below what the other buyers on the same private contracts bid wins little or nothing on this inventory. APPLIES TO LIVE CAMPAIGNS, same lane as the base bid.',
  'ttd.display_base_bid_cpm_cents':
    'Starting bid for every Display ad group, as CPM in cents. Display buys through the same deal list as CTV. APPLIES TO LIVE CAMPAIGNS: the TTD reconcile lane brings published Display ad groups to this value. Must be positive and no greater than the max; either failure blocks a Display campaign at preflight.',
  'ttd.display_max_bid_cpm_cents':
    'Bid ceiling for every Display ad group, as CPM in cents. APPLIES TO LIVE CAMPAIGNS, same lane as the base bid.',
  'conversions.uid2.base_url':
    'UID2 operator base URL. Must be a uidapi.com host.',
  'conversions.uid2.map_batch_size':
    'Distinct identity hashes per /v3/identity/map call (1-1000). Purely a packing factor — correspondence is positional and the client refuses a length mismatch, so keep it conservative. It no longer changes any coverage ceiling: the mint budgets below are expressed in identities and divide by this.',
  'conversions.uid2.refresh_staleness_days':
    'Fallback staleness window for stored UID2s whose operator refresh_from is absent. Leave empty to trust refresh_from alone.',
  'conversions.uid2.refresh_max_calls_per_run':
    'Outbound identity/map call budget for one uid2_refresh run — the USERS sweep only (the prospect lane has its own job and its own budget). Default 10,000 since 2026-10-15; a saved configuration keeps the value it was saved with until you change it here. Each call carries up to map_batch_size identities. The run refreshes the users waiting longest first; unfinished work resumes next run.',
  'conversions.uid2.prospect_refresh_max_calls_per_run':
    'The same budget for uid2_prospect_refresh, which runs hourly over uid2_prospect. Separate from the users budget on purpose: while the two shared one, users held ~3.85M rows against a 4,000-call ceiling, was truncated every night, and the prospect lane only ever got its reserved floor — so prospect audiences reached TTD at a fraction of their size and stayed there.',
  'conversions.uid2.prospect_inline_mint_max_identities':
    'How many identities one prospect TTD upload may mint inline for cache misses. This is the ceiling on how much of a fresh audience reaches TTD on its FIRST drop — everything beyond it is seeded pending for uid2_prospect_refresh and reported as skipped_no_uid2. People carry roughly 1.5 identities each, so 100,000 covers about 65,000 people. Set to 0 to disable inline minting entirely and let the hourly job do all of it.',
  'conversions.uid2.prospect_inline_mint_max_seconds':
    'Seconds of UID2 operator latency the same inline mint may spend, whichever bound comes first. A second bound in different units: the identity budget caps the work attempted, not how long a degraded operator takes to refuse it, and up to four audience drops run concurrently in one process. It counts time inside identity/map calls only — reading the prospect pages from the data store is not charged, so a busy store cannot truncate the mint.',
  'llm.brief_model':
    'The Claude model that writes the Daily Briefing\u2019s sentences. It runs in the background, so a slower, stronger model costs time nobody waits on. Prices are list prices per million tokens; the options below name each model\u2019s price and the effort it uses when none is chosen.',
  'llm.brief_effort':
    'How much the Daily Briefing\u2019s model may think before it writes. Higher effort reads the facts more carefully and costs more output tokens; thinking counts against Brief Max Tokens.',
  'llm.brief_max_tokens':
    'The most tokens one Daily Briefing call may produce, thinking included (1,024 to 21,000). A reply cut off at this budget is retried once with double the budget, capped at 21,000 tokens; a budget already at 21,000 is not retried.',
  'llm.brief_timeout_secs':
    'How long the whole Daily Briefing exchange may take, retries included (30\u2013600 s). It runs in the background, so nobody waits on it; past this the briefing keeps its facts and drops the sentences.',
  'llm.explain_model':
    'The Claude model behind the Explain panels. An Explain answers someone waiting on the page, so it must finish inside the 30 s each request is given; a fast model at low effort is the default for that reason.',
  'llm.explain_effort':
    'How much the Explain model may think before it answers. Higher effort is slower and costs more output tokens; at high effort an Explain can miss its deadline and fall back to the facts-only answer.',
  'llm.explain_max_tokens':
    'The most tokens one Explain call may produce, thinking included (1,024 to 21,000). A reply cut off at this budget is retried once with double the budget, capped at 21,000 tokens and inside the same deadline.',
  'llm.explain_timeout_secs':
    'How long the whole Explain exchange may take, the wait for a free slot and a retry included (5\u201328 s). The answer travels through SAM Server\u2019s API, whose budget per request is 30 s; past this the panel shows its facts-only answer.',
  'llm.max_concurrent':
    'Claude calls in flight at once, per task (1\u20138). The Daily Briefing and the Explain panels each have their own slots, so a running briefing never makes an Explain wait.',
  'llm.cache_ttl':
    'How long Claude keeps the cached instructions every call starts with. Caching is always on: a cached read costs a small fraction of the input price. 5m suits Explains clicked in a burst; 1h costs twice the input price to write instead of 1.25\u00d7, and pays off only when calls come more than five minutes apart but within the hour.',
  'llm.refusal_fallback':
    'When Claude declines a request, ask it to answer on its recommended fallback model instead of returning the refusal. The fallback is billed at its own model\u2019s price. Claude Haiku 5.5 has no fallback, so this does nothing for a task on Haiku.',
  'llm.api_key':
    'Claude API key from console.anthropic.com. A monthly spend limit is set there too; SMART records each call\u2019s cost but sets no cap of its own.',
};

/// Every enum the editor renders as a select, with what each value does.
/// A value the operator can pick without being told what it means is a value
/// picked by guessing; the description renders under the select and the whole
/// list renders in the help block.
interface EnumOption { value: string; label: string; description: string }

/// The Claude models an Admin may pick for a task, read from the one catalog
/// (`shared/specs/claude-models.json` through `@/lib/claude-models`): labels,
/// list prices, the long-prompt tier, default efforts and which model has no
/// refusal fallback are written there once. admin-config.llm-fields.guard.test.ts
/// holds the values to the `ClaudeModel` binding and the catalog.
const CLAUDE_MODEL_OPTIONS: EnumOption[] = [...CLAUDE_MODEL_SELECT_OPTIONS];

/// `ClaudeEffort`: every selectable model accepts all five.
const CLAUDE_EFFORT_OPTIONS: EnumOption[] = [
  { value: 'low', label: 'Low', description: 'Fastest and cheapest: the least thinking and the shortest answers.' },
  { value: 'medium', label: 'Medium', description: 'A balance of care, speed and cost.' },
  { value: 'high', label: 'High', description: 'Thinks and writes more before it is done: slower, and more output tokens.' },
  { value: 'xhigh', label: 'Extra high', description: 'More thinking still; on a hard question much of max tokens can go to it.' },
  { value: 'max', label: 'Max', description: 'The most thinking the model allows: the slowest, the most expensive and the likeliest to run out of max tokens.' },
];

const ENUM_OPTIONS: Record<string, EnumOption[]> = {
  'delivery_reporting.ttd_intraday_mode': [
    { value: 'off', label: 'Off', description: 'Makes no intraday requests.' },
    { value: 'shadow', label: 'Shadow', description: 'Collects and reconciles current-day delivery without publishing it.' },
    { value: 'publish', label: 'Publish', description: 'Writes provisional current-day delivery through the canonical reporting tables.' },
  ],
  'llm.brief_model': CLAUDE_MODEL_OPTIONS,
  'llm.brief_effort': CLAUDE_EFFORT_OPTIONS,
  'llm.explain_model': CLAUDE_MODEL_OPTIONS,
  'llm.explain_effort': CLAUDE_EFFORT_OPTIONS,
  'llm.cache_ttl': [
    { value: '5m', label: '5 minutes', description: 'Writing the cache costs 1.25\u00d7 the input price; each read within five minutes of the last use costs a fraction of it.' },
    { value: '1h', label: '1 hour', description: 'Writing the cache costs 2\u00d7 the input price; reads stay cheap for an hour after the last use.' },
  ],
};

/// Enums selected by path suffix, for fields several sections share.
const ENUM_SUFFIX_OPTIONS: { suffix: string; options: EnumOption[] }[] = [
  {
    suffix: '.region',
    options: [
      { value: 'US', label: 'US', description: 'United States region.' },
      { value: 'EU', label: 'EU', description: 'European Union region.' },
      { value: 'APAC', label: 'APAC', description: 'Asia-Pacific region.' },
    ],
  },
  {
    suffix: '.mode',
    options: [
      { value: 'log_only', label: 'Log only', description: 'Runs the lane and records what it would send; nothing leaves the system.' },
      { value: 'live', label: 'Live', description: 'Sends for real.' },
    ],
  },
];

function enumOptionsFor(path: string): EnumOption[] | undefined {
  return ENUM_OPTIONS[path]
    ?? ENUM_SUFFIX_OPTIONS.find((entry) => path.endsWith(entry.suffix))?.options;
}

interface ArrayDraftContextValue {
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (path: string, text: string) => void;
}

const ArrayDraftContext = createContext<ArrayDraftContextValue | null>(null);

/// Dotted paths the last Validate or Apply rejected, so each field row can mark
/// itself instead of relying on the summary alone.
const InvalidPathsContext = createContext<ReadonlySet<string>>(new Set());

const OPTIONAL_DEFAULTS: Record<string, ConfigValue> = {
  encorepro_postgres: {
    host: '', port: 5432, database: '', user: '', sslmode: 'require',
    pool_size: 8, rate_limit_rps: 4, schema: null, tenant_id: '',
    connect_retry_interval_secs: 30, connect_timeout_secs: 30,
    connect_max_attempts: 60,
  },
  log_triage: {
    lookback_hours: 168, max_lines_per_environment: 10000, max_groups: 100,
    samples_per_group: 3, trace_lines_per_group: 50,
    output_dir: '/var/lib/log-triage',
    environments: [
      { name: 'prod', schema: 'sam_prod', production: true },
      { name: 'dev', schema: 'sam_dev', production: false },
    ],
  },
  delivery_reporting: {
    enabled: false, lookback_days: 31, settled_lag_days: 30,
    delivery_settled_lag_days: 0,
    max_mappings_per_run: 500, max_pages_per_mapping: 20,
    meta_page_size: 100, ttd_creative_page_size: 50,
    ttd_report_timeout_minutes: 120, ttd_delivery_template_id: null,
    ttd_attribution_template_id: null, ttd_intraday_mode: 'off',
  },
  email: {
    api_base: 'https://api.cloudflare.com/client/v4', account_id: '', from: '', analytics_zone_id: '',
  },
  alerts: {
    enabled: true, email_to: [], console_url: 'https://app.shubertads.com/backend/alerts',
    spike_threshold: 5, scan_window_minutes: 10, suppression_hours: 6,
  },
  log_archive: { enabled: true, prefix: 'logs', retention_days: null },
  conversions: {
    enabled: false, mode: 'log_only', meta: null, ttd: null, uid2: null,
    delivery: {
      claim_batch: 2000, max_attempts: 5, retry_base_secs: 60,
      freshness_max_hours: null, meta_freshness_max_hours: 156,
      ttd_freshness_max_hours: 720, retention_days: 30,
      event_types: ['Purchase'], max_posts_per_tick: 30,
    },
    tickets: null,
  },
  'conversions.meta': {
    // Must match what the workers call. This template said v25.0 until
    // 2026-08-17: enabling the section from here would silently pin conversions
    // to a version behind the one every other Meta call uses, and it passes
    // validation, so nothing would have said so. The Rust default is v26.0
    // (batch-worker/src/config.rs, default_conversions_graph_base).
    graph_api_base: 'https://graph.facebook.com/v26.0',
    test_event_code: null, batch_size: 200,
  },
  'conversions.ttd': {
    endpoint: 'https://offlineattrib.adsrvr.org/providerapi/offlineconversion',
    data_provider_id: '', merchant_id: null, batch_size: 50,
  },
  'conversions.uid2': {
    base_url: '', map_batch_size: 100,
    refresh_staleness_days: null, refresh_max_calls_per_run: 10000,
    prospect_refresh_max_calls_per_run: 2000,
    prospect_inline_mint_max_identities: 100000,
    prospect_inline_mint_max_seconds: 120,
  },
  'conversions.tickets': {
    enabled: false, pixel_id: '', meta: true, ttd: true,
    test_event_code: null, batch: 5000, action_source: 'website',
  },
  llm: {
    brief_model: 'claude-sonnet-5-5', brief_effort: 'medium', brief_max_tokens: 16000, brief_timeout_secs: 300,
    explain_model: 'claude-sonnet-5-5', explain_effort: 'low', explain_max_tokens: 4096, explain_timeout_secs: 25,
    max_concurrent: 2, cache_ttl: '5m', refusal_fallback: true,
  },
  copilot: {
    enabled: false, cache_retention_days: 30, cache_fresh_secs: 300,
    brief_enabled: false, brief_notify: false,
  },
  invoicing: {
    enabled: false, xero_client_id: '', xero_contact_id: '',
    xero_api_base: 'https://api.xero.com', xero_identity_base: 'https://identity.xero.com',
    xero_account_code: '', xero_item_code: '', xero_fee_item_code: '', management_fee_bps: 0,
    xero_invoice_number_prefix: 'SHUBERT',
    xero_tax_type: 'NONE',
    run_weekday: 2, auto_approve: false,
    recipients_to: [], recipients_cc: [], reference_prefix: 'SMART',
    email_subject_template: 'Invoice {reference} - {period}', first_period: '',
    catch_up_weeks: 8,
    adjustments_enabled: false, adjustment_min_cents: 2500, adjustment_window_weeks: 12,
    adjustment_email_subject_template: 'Adjustment invoice {reference} - {period}',
  },
  // pixel-worker's KV projection (shared::ManagedPixelKvConfig). The namespace is
  // the PIXEL_CACHE id of the environment this batch-worker serves.
  pixel_kv: {
    enabled: false, api_base: 'https://api.cloudflare.com/client/v4', account_id: '', namespace_id: '',
  },
};

/// Narrow a section object to one sub-tab's keys. Paths are rebuilt from the
/// unchanged section path, so every field keeps its real dotted path and edits
/// land in the right place in the draft.
function pickKeys(value: ConfigValue | undefined, keys: readonly string[]): ConfigValue | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const out: Record<string, ConfigValue> = {};
  for (const key of keys) {
    if (key in value) out[key] = (value as Record<string, ConfigValue>)[key]!;
  }
  return out;
}

const OPTIONAL_OBJECT_PATHS = new Set(Object.keys(OPTIONAL_DEFAULTS));
const NUMERIC_NULL_PATHS = new Set([
  'delivery_reporting.ttd_delivery_template_id',
  'delivery_reporting.ttd_attribution_template_id',
  'conversions.delivery.freshness_max_hours',
  // Option<usize> / Option<u32> in ManagedEnrichConfig. Omitting them here made a
  // null field render as text and emit a string, which cannot deserialize.
  'enrich.max_batch',
  'enrich.max_concurrent_requests',
  // Option<i64> in ManagedUid2Config, and it defaults to null — so it had the
  // same latent defect as the two above and nobody had tried to set it.
  'conversions.uid2.refresh_staleness_days',
  // Option<i64> in ManagedLogArchiveConfig. It was also missing from
  // OPTIONAL_DEFAULTS until 2026-08-28, so Configure never created the key and
  // the setting could not be reached at all. admin-config.nullable.guard.test.ts
  // now derives both lists from the generated bindings.
  'log_archive.retention_days',
]);
const NULLABLE_SCALAR_PATHS = new Set([
  'enrich.max_batch',
  'enrich.max_concurrent_requests',
  'encorepro_postgres.schema',
  'r2.endpoint',
  'delivery_reporting.ttd_delivery_template_id',
  'delivery_reporting.ttd_attribution_template_id',
  'conversions.meta.test_event_code',
  'conversions.ttd.merchant_id',
  'conversions.delivery.freshness_max_hours',
  // Option<i64>: without this entry the field had no Clear button and a blank
  // input was ignored, so once set it could never return to null.
  'conversions.uid2.refresh_staleness_days',
  'log_archive.retention_days',
  'conversions.tickets.test_event_code',
]);

/// `NULLABLE_SCALAR_PATHS` cannot enumerate the scheduler crons without drifting
/// from `ManagedSchedulerConfig`, and every one of them is `Option<String>` whose
/// `None` means "use the job's built-in default cron".
function isNullablePath(path: string): boolean {
  return NULLABLE_SCALAR_PATHS.has(path) || /^scheduler\.[a-z0-9_]+_cron$/.test(path);
}

/// Blank means "unset" for a nullable field; `Number('')` would silently write 0.
/// Returns `undefined` for input that is not a number *yet* (`-`, `1e`), so a
/// partially-typed value is neither written to the draft nor snapped away.
function parseNumericInput(path: string, raw: string): ConfigValue | undefined {
  if (raw.trim() === '') return isNullablePath(path) ? null : undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/// Numeric fields keep their raw text while focused.
///
/// A controlled `value={String(value)}` round-trip erases a trailing decimal
/// point, a leading minus or an exponent on every keystroke, so a value could
/// never be typed through one of those intermediate states.
function NumericInput({
  path, value, onChange,
}: {
  path: string; value: number;
  onChange: (path: string, value: ConfigValue) => void;
}) {
  const [raw, setRaw] = useState<string | null>(null);
  return (
    <input
      id={configFieldId(path)}
      data-config-path={path}
      className="input"
      type="text"
      inputMode="decimal"
      value={raw ?? String(value)}
      onChange={(event) => {
        setRaw(event.target.value);
        const parsed = parseNumericInput(path, event.target.value);
        if (parsed !== undefined) onChange(path, parsed);
      }}
      onBlur={() => setRaw(null)}
    />
  );
}

interface AdminConfigViewProps {
  search: AdminConfigSearch;
  onSearchChange: (
    patch: Partial<AdminConfigSearch>,
    options?: { replace?: boolean },
  ) => void;
}

interface ConfigValidationItem {
  path: string;
  message: string;
}

export function AdminConfigView({ search, onSearchChange }: AdminConfigViewProps) {
  const phone = usePhone();
  const query = useAdminConfig();
  const validate = useValidateAdminConfig();
  const save = useSaveAdminConfig();
  const retry = useRetryAdminConfig();
  const [draft, setDraft] = useState<ConfigDocument | null>(null);
  const [baseDocument, setBaseDocument] = useState<ConfigDocument | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [secretValues, setSecretValues] = useState<Partial<Record<ConfigSecretPath, string>>>({});
  const [clearedSecrets, setClearedSecrets] = useState<Set<ConfigSecretPath>>(new Set());
  const [arrayDrafts, setArrayDrafts] = useState<Record<string, string>>({});
  const [arrayErrors, setArrayErrors] = useState<Record<string, string>>({});
  const [validated, setValidated] = useState(false);
  const [pendingFocusPath, setPendingFocusPath] = useState<string | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const handledValidationRef = useRef('');
  // The apply modal is a full-screen scrim. It must be escapable: before the
  // control plane grew a stall timeout, an unreachable batch worker left this
  // screen covered with no dismiss, no error, and no available action.
  const [applyDismissed, setApplyDismissed] = useState(false);

  const documentDirty = draft !== null && baseDocument !== null
    && !configValuesEqual(draft, baseDocument);
  const secretsDirty = Object.values(secretValues).some(Boolean) || clearedSecrets.size > 0;
  const hasArrayErrors = Object.keys(arrayErrors).length > 0;
  const dirty = documentDirty || secretsDirty || hasArrayErrors;

  const section = getConfigSection(search.section) ?? getConfigSection('meta')!;
  const activeTab = getConfigTab(section, search.tab) ?? section.tabs[0]!;
  const selected = section.key;

  // `desired_version` is a dependency in its own right: a secrets-only save
  // (or another administrator saving an identical document) advances the
  // pointer while structural sharing hands back the same `document` reference,
  // and a base version pinned to the old pointer turns the next save into a
  // real conflict.
  useEffect(() => {
    const responseVersion = query.data?.state.desired_version ?? null;
    const responseIsCurrent = baseVersion === null
      || (responseVersion !== null && responseVersion >= baseVersion);
    if (query.data?.document && !dirty && responseIsCurrent) {
      const nextDocument = structuredClone(query.data.document);
      setDraft(nextDocument);
      setBaseDocument(structuredClone(nextDocument));
      setBaseVersion(responseVersion);
      setArrayDrafts((current) => Object.keys(current).length > 0 ? {} : current);
      setArrayErrors((current) => Object.keys(current).length > 0 ? {} : current);
    }
  }, [baseVersion, query.data?.document, query.data?.state.desired_version, dirty]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    // While this prompt is armed no page change loads a new release as a document: its Stay
    // would strand the router on a committed location (frontend-release-update, 2026-10-21).
    const disarm = dirty ? registerUnloadPrompt() : undefined;
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      disarm?.();
    };
  }, [dirty]);

  const transient = query.data
    && ['pending', 'preparing', 'applying'].includes(query.data.state.status);
  useEffect(() => {
    if (!transient) setApplyDismissed(false);
  }, [transient]);

  const validationErrors = useMemo(
    () => configValidationItems(validate.error ?? save.error),
    [validate.error, save.error],
  );
  const invalidPaths = useMemo(
    () => new Set(validationErrors.map((item) => item.path)),
    [validationErrors],
  );
  // The search index walks the whole document and concatenates every help
  // string. Rebuild it when the draft changes, not on every keystroke, poll
  // tick or tab change.
  const searchEntries = useMemo(
    () => (draft ? buildConfigSearchEntries(draft) : []),
    [draft],
  );

  const findValidationLocation = useCallback((path: string) => {
    return configValidationLocation(path);
  }, []);
  const findValidationSection = useCallback((path: string) => {
    return configValidationSection(path);
  }, []);

  // A hidden invalid setting is worse than a long form: take the operator to
  // the first concrete error, then let the linked summary reach every sibling.
  useEffect(() => {
    if (validationErrors.length === 0) {
      handledValidationRef.current = '';
      return;
    }
    const signature = validationErrors.map((item) => `${item.path}:${item.message}`).join('|');
    if (handledValidationRef.current === signature) return;
    handledValidationRef.current = signature;
    const first = validationErrors.find((item) => findValidationLocation(item.path))
      ?? validationErrors.find((item) => findValidationSection(item.path));
    if (!first) return;
    const targetSection = findValidationSection(first.path)!;
    const location = findValidationLocation(first.path);
    const targetTab = location?.tab ?? targetSection.tabs[0]!;
    setPendingFocusPath(location ? first.path : null);
    if (targetSection.key !== selected || targetTab.id !== activeTab.id) {
      // A corrective move, not an operator choice: replace so Back leaves the
      // screen rather than returning to the tab that failed validation.
      onSearchChange({
        section: targetSection.key as AdminConfigSearch['section'],
        tab: targetTab.id,
      }, { replace: true });
    }
  }, [activeTab.id, findValidationLocation, findValidationSection, onSearchChange, selected, validationErrors]);

  useEffect(() => {
    if (!pendingFocusPath) return;
    const location = findValidationLocation(pendingFocusPath);
    if (!location || location.section.key !== selected || location.tab.id !== activeTab.id) return;
    const frame = window.requestAnimationFrame(() => {
      const target = findConfigFieldElement(pendingFocusPath);
      if (!target) return;
      target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      target.focus({ preventScroll: true });
      setPendingFocusPath(null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab.id, findValidationLocation, pendingFocusPath, selected]);

  if (query.error) {
    return (
      <div className="tbl-frame">
        <div style={{ padding: 28 }}>
          <QueryErrorNote error={query.error} what="configuration" onRetry={() => void query.refetch()} />
        </div>
      </div>
    );
  }
  if (query.isLoading || !draft) {
    return <div className="tbl-frame"><div style={{ padding: 28 }}>Loading configuration…</div></div>;
  }

  const body = (): AdminConfigWirePut => {
    const updates: ConfigSecretUpdate[] = [];
    for (const path of CONFIG_SECRET_PATHS) {
      if (clearedSecrets.has(path)) updates.push({ action: 'clear', path });
      else if (secretValues[path]) updates.push({ action: 'set', path, value: secretValues[path]! });
    }
    return {
      expected_version: baseVersion,
      document: draft,
      secret_updates: updates,
    };
  };

  const invalidateValidation = () => {
    setValidated(false);
    validate.reset();
    save.reset();
  };

  const updateValue = (path: string, value: ConfigValue) => {
    setDraft((current) => {
      if (!current) return current;
      const next = structuredClone(current);
      setAtPath(next, path.split('.'), value);
      return next;
    });
    invalidateValidation();
  };

  const updateArrayDraft = (path: string, text: string) => {
    setArrayDrafts((current) => ({ ...current, [path]: text }));
    try {
      const parsed: unknown = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('Must be a JSON array');
      setArrayErrors((current) => {
        if (!(path in current)) return current;
        const next = { ...current };
        delete next[path];
        return next;
      });
      updateValue(path, parsed as ConfigValue[]);
    } catch (caught) {
      setArrayErrors((current) => ({ ...current, [path]: (caught as Error).message }));
      invalidateValidation();
    }
  };

  // Both mutations surface their failure through `MutationErrorLine`
  // (`validate.error` / `save.error`); the promise the button fires is
  // otherwise unobserved, so a rejection here used to reach the window as an
  // unhandled rejection on top of the rendered message.
  const validateDraft = async (): Promise<boolean> => {
    if (hasArrayErrors) return false;
    try {
      await validate.mutateAsync(body());
    } catch {
      return false;
    }
    setValidated(true);
    return true;
  };

  const saveDraft = async () => {
    if (!validated && !(await validateDraft())) return;
    if (!window.confirm('Apply this configuration now? Running work will finish on its current settings; new work will use the new version.')) return;
    let committed;
    try {
      committed = await save.mutateAsync(body());
    } catch {
      return;
    }
    // Rebase on the response, not on the next refetch: the pointer has moved
    // even when the document bytes did not.
    setBaseVersion(committed.state.desired_version ?? committed.revision.version);
    setBaseDocument(structuredClone(draft));
    setValidated(false);
    setSecretValues({});
    setClearedSecrets(new Set());
    setArrayDrafts({});
    setArrayErrors({});
    validate.reset();
    save.reset();
  };

  const activeSection = draft[selected];
  const secretMeta = new Map(query.data?.secrets.map((item) => [item.path, item]));

  const tabIsDirty = (candidateSection: ConfigSectionDefinition, candidateTab: ConfigTabDefinition) => {
    const currentSection = draft[candidateSection.key];
    const originalSection = baseDocument?.[candidateSection.key];
    const currentObject = isConfigObject(currentSection) ? currentSection : {};
    const originalObject = isConfigObject(originalSection) ? originalSection : {};
    return candidateTab.fieldKeys.some((key) => (
      !configValuesEqual(currentObject[key], originalObject[key])
      || Object.keys(arrayErrors).some((path) => (
        path === `${candidateSection.key}.${key}`
        || path.startsWith(`${candidateSection.key}.${key}.`)
      ))
    )) || candidateTab.secretPaths.some((path) => (
      Boolean(secretValues[path]) || clearedSecrets.has(path)
    ));
  };

  const tabErrors = (candidateSection: ConfigSectionDefinition, candidateTab: ConfigTabDefinition) => (
    validationErrors.filter((item) => {
      const location = findValidationLocation(item.path);
      return location?.section.key === candidateSection.key && location.tab.id === candidateTab.id;
    })
  );

  const sectionErrors = (candidateSection: ConfigSectionDefinition) => (
    validationErrors.filter((item) => findValidationSection(item.path)?.key === candidateSection.key)
  );

  const focusValidationItem = (item: ConfigValidationItem) => {
    const location = findValidationLocation(item.path);
    if (!location) return;
    setPendingFocusPath(item.path);
    onSearchChange({
      section: location.section.key as AdminConfigSearch['section'],
      tab: location.tab.id,
    });
  };

  const selectSearchEntry = (entry: ConfigSearchEntry) => {
    setPendingFocusPath(entry.path);
    onSearchChange({ section: entry.section as AdminConfigSearch['section'], tab: entry.tab });
  };

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % section.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + section.tabs.length) % section.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = section.tabs.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = section.tabs[nextIndex]!;
    onSearchChange({ tab: next.id });
    window.requestAnimationFrame(() => tabRefs.current[nextIndex!]?.focus());
  };

  return (
    <div className="tbl-frame admin-config-page">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Config</h1>
          <div className="desktop-only" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 4 }}>
            Runtime settings · hot reload · desired and applied revisions
          </div>
        </div>
        {/* Desktop keeps the search beside the actions; a phone folds it into the ⋯ sheet. */}
        {!phone && <ConfigSettingSearch entries={searchEntries} onSelect={selectSearchEntry} />}
        <PageActions>
          {phone && <ConfigSettingSearch entries={searchEntries} onSelect={selectSearchEntry} />}
          <button
            className="btn"
            disabled={!dirty || validate.isPending || hasArrayErrors}
            title={hasArrayErrors ? 'Fix invalid JSON arrays before validating' : undefined}
            onClick={() => void validateDraft()}
          >
            {validate.isPending ? 'Validating…' : validated ? 'Validated ✓' : 'Validate'}
          </button>
          <button
            className="btn btn-primary"
            data-phone="inline"
            disabled={!dirty || save.isPending || hasArrayErrors}
            title={hasArrayErrors ? 'Fix invalid JSON arrays before applying' : undefined}
            onClick={() => void saveDraft()}
          >
            {save.isPending ? 'Saving…' : <><span className="phone-long">Validate &amp; apply</span><span className="phone-short">Apply</span></>}
          </button>
        </PageActions>
      </div>

      <ConfigStatusBar
        status={query.data!.state.status}
        desired={query.data!.state.desired_version}
        applied={query.data!.state.applied_version}
        error={query.data!.state.last_error}
        nodes={query.data!.nodes ?? []}
        conflict={dirty && query.data!.state.desired_version !== baseVersion}
        baseVersion={baseVersion}
        onRetry={() => retry.mutate()}
        retrying={retry.isPending}
      />

      <div className="admin-config-workspace">
        <ResponsiveRail className="admin-config-rail" aria-label="Configuration sections">
          {CONFIG_NAVIGATION.map((group) => (
            <div key={group.id} className="admin-config-group">
              <div className="mono admin-config-group-label">{group.label}</div>
              {group.sections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => onSearchChange({
                    section: section.key as AdminConfigSearch['section'],
                    tab: section.tabs[0]!.id,
                  })}
                  className={`admin-config-section ${selected === section.key ? 'on' : ''}`}
                  aria-current={selected === section.key ? 'page' : undefined}
                >
                  <span className="admin-config-section-label">{section.label}</span>
                  <span className="admin-config-section-meta" aria-hidden="true">
                    {sectionErrors(section).length > 0 && (
                      <span className="admin-config-error-badge" title="Contains validation errors">!</span>
                    )}
                    {section.tabs.some((candidate) => tabIsDirty(section, candidate)) && (
                      <span className="admin-config-dirty-badge" title="Contains unsaved changes">●</span>
                    )}
                    <span className={draft[section.key] === null || draft[section.key] === undefined ? 'is-off' : 'is-on'}>
                      {draft[section.key] === null || draft[section.key] === undefined ? '○' : '●'}
                    </span>
                  </span>
                  <span className="sr-only">
                    {draft[section.key] === null || draft[section.key] === undefined ? 'Not configured' : 'Configured'}
                    {section.tabs.some((candidate) => tabIsDirty(section, candidate)) ? ', unsaved changes' : ''}
                    {sectionErrors(section).length > 0 ? ', contains validation errors' : ''}
                  </span>
                </button>
              ))}
            </div>
          ))}
          <div className="admin-config-locked-summary" title="Startup-only settings; edit the bootstrap TOML on the SAM Server host">
            <div className="mono admin-config-group-label">Bootstrap · TOML</div>
            <div>🔒 API · Data Warehouse · Data Lake · State</div>
          </div>
        </ResponsiveRail>

        <main className="admin-config-editor">
          <div className="admin-config-editor-head">
            <div className="admin-config-editor-title">
              <div className="mono admin-config-editor-kicker">MANAGED SECTION</div>
              <div className="admin-config-section-title-row">
                <h2>{section.label}</h2>
                {section.tabs.some((candidate) => tabIsDirty(section, candidate)) && (
                  <span className="admin-config-unsaved-label">Unsaved changes</span>
                )}
              </div>
              <div className="admin-config-editor-description">
                Stored as part of one versioned document. Changes become visible atomically.
              </div>
            </div>

            {!section.compact && (
              <div className="admin-config-tabs-wrap">
                <div
                  className="admin-config-tablist"
                  role="tablist"
                  aria-label={`${section.label} settings`}
                  aria-orientation="horizontal"
                >
                  {section.tabs.map((candidate, index) => {
                    const active = candidate.id === activeTab.id;
                    const owned = candidate.fieldKeys
                      .map((key) => isConfigObject(activeSection) ? activeSection[key] : undefined)
                      .filter((value) => value !== undefined);
                    const off = candidate.fieldKeys.length > 0 && (
                      activeSection === null
                      || (owned.length > 0 && owned.every((value) => value === null))
                    );
                    const errors = tabErrors(section, candidate);
                    return (
                      <button
                        key={candidate.id}
                        type="button"
                        role="tab"
                        id={`admin-config-tab-${section.key}-${candidate.id}`}
                        aria-selected={active}
                        aria-keyshortcuts="ArrowLeft ArrowRight Home End"
                        tabIndex={active ? 0 : -1}
                        ref={(element) => { tabRefs.current[index] = element; }}
                        onClick={() => onSearchChange({ tab: candidate.id })}
                        onKeyDown={(event) => onTabKeyDown(event, index)}
                        className={off ? 'admin-config-tab is-off' : 'admin-config-tab'}
                      >
                        <span>{candidate.label}</span>
                        {tabIsDirty(section, candidate) && (
                          <>
                            <span className="admin-config-dirty-badge" title="Unsaved changes" aria-hidden="true">●</span>
                            <span className="sr-only">Unsaved changes.</span>
                          </>
                        )}
                        {errors.length > 0 && (
                          <>
                            <span
                              className="admin-config-error-badge"
                              title={`${errors.length} validation error${errors.length === 1 ? '' : 's'}`}
                              aria-hidden="true"
                            >
                              {errors.length}
                            </span>
                            <span className="sr-only">
                              {errors.length} validation error{errors.length === 1 ? '' : 's'}.
                            </span>
                          </>
                        )}
                        {off && <span className="admin-config-off-badge" title="Section disabled">Off</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            id="admin-config-tabpanel"
            className="admin-config-tab-panel"
            role={section.compact ? undefined : 'tabpanel'}
            aria-labelledby={section.compact ? undefined : `admin-config-tab-${section.key}-${activeTab.id}`}
            tabIndex={0}
          >
            <MutationErrorLine error={validate.error ?? save.error ?? retry.error} />
            <ValidationDetails errors={validationErrors} onActivate={focusValidationItem} />
            {/* What the section's settings spend, above every AI/LLM tab: read-only, outside the draft. */}
            {selected === 'llm' && <LlmSpendPanel />}

            <InvalidPathsContext.Provider value={invalidPaths}>
            <ArrayDraftContext.Provider value={{
              values: arrayDrafts,
              errors: arrayErrors,
              onChange: updateArrayDraft,
            }}>
                {activeSection === null && OPTIONAL_DEFAULTS[selected] ? (
                  <EmptySection
                    label={section.label}
                    path={selected}
                    onEnable={() => updateValue(selected, structuredClone(OPTIONAL_DEFAULTS[selected]!))}
                  />
                ) : activeTab.fieldKeys.length > 0 ? (
                  <ValueEditor
                    path={selected}
                    value={pickKeys(activeSection, activeTab.fieldKeys)}
                    onChange={updateValue}
                  />
                ) : null}
            </ArrayDraftContext.Provider>
            </InvalidPathsContext.Provider>

            {activeTab.secretPaths.length > 0 && (
              <section className="admin-config-credentials" aria-label={`${section.label} credentials`}>
                <div className="admin-config-credentials-head">
                  <h3>Credentials</h3>
                  <p>Existing values are never returned. Leave blank to keep the configured value.</p>
                </div>
                {activeTab.secretPaths.map((path) => {
                  const meta = secretMeta.get(path);
                  const clearing = clearedSecrets.has(path);
                  const inputId = configFieldId(path);
                  return (
                    <div key={path} className="admin-config-field-row">
                      <label className="admin-config-field-label" htmlFor={inputId}>
                        {fieldLabel(path)}
                      </label>
                      <div className="admin-config-field-control">
                        <div className="admin-config-secret-control">
                          <input
                            id={inputId}
                            data-config-path={path}
                            className="input"
                            type="password"
                            autoComplete="new-password"
                            value={secretValues[path] ?? ''}
                            disabled={clearing}
                            placeholder={meta?.is_set ? '••••••••  configured' : 'Not configured'}
                            onChange={(event) => {
                              setSecretValues((current) => ({ ...current, [path]: event.target.value }));
                              invalidateValidation();
                            }}
                          />
                          <button
                            className="btn"
                            type="button"
                            onClick={() => {
                              setClearedSecrets((current) => {
                                const next = new Set(current);
                                if (next.has(path)) next.delete(path); else next.add(path);
                                return next;
                              });
                              invalidateValidation();
                            }}
                          >
                            {clearing ? 'Undo clear' : 'Clear'}
                          </button>
                        </div>
                        <div className="mono admin-config-secret-meta">
                          {meta?.is_set
                            ? meta.updated_at
                              ? `set ${fmtStampEt(meta.updated_at)}`
                              : 'set · rotation time unknown'
                            : 'never set'}
                        </div>
                        <FieldHelp path={path} />
                      </div>
                    </div>
                  );
                })}
              </section>
            )}
          </div>
        </main>
      </div>

      {(save.isPending || transient) && !applyDismissed && (
        <ApplyModal
          status={query.data!.state.status}
          desired={query.data!.state.desired_version}
          onDismiss={() => setApplyDismissed(true)}
        />
      )}
    </div>
  );
}

function ValueEditor({
  path,
  value,
  onChange,
}: {
  path: string;
  value: ConfigValue | undefined;
  onChange: (path: string, value: ConfigValue) => void;
}) {
  if (value === undefined) return null;
  if (value === null) {
    const configured = OPTIONAL_DEFAULTS[path];
    if (configured) return (
      <EmptySection
        label={fieldLabel(path)}
        path={path}
        onEnable={() => onChange(path, structuredClone(configured))}
      />
    );
    return (
      <Field label={fieldLabel(path)} path={path}>
        <input
          id={configFieldId(path)}
          data-config-path={path}
          className="input"
          value=""
          placeholder="Not set"
          type="text"
          inputMode={NUMERIC_NULL_PATHS.has(path) ? 'decimal' : undefined}
          onChange={(event) => {
            if (!NUMERIC_NULL_PATHS.has(path)) {
              onChange(path, event.target.value);
              return;
            }
            const parsed = parseNumericInput(path, event.target.value);
            if (parsed !== undefined) onChange(path, parsed);
          }}
        />
      </Field>
    );
  }
  if (Array.isArray(value)) {
    return <ArrayEditor path={path} value={value} />;
  }
  if (typeof value === 'object') {
    const fields = (
      <div style={{ display: 'grid', gap: 10 }}>
        {Object.entries(value).map(([key, child]) => (
          <ValueEditor key={key} path={`${path}.${key}`} value={child} onChange={onChange} />
        ))}
      </div>
    );
    return (
      <>
        {fields}
        {OPTIONAL_OBJECT_PATHS.has(path) && (
          <button
            id={configFieldId(path)}
            data-config-path={path}
            className="btn"
            style={{ marginTop: 12 }}
            type="button"
            onClick={() => onChange(path, null)}
          >
            Disable {fieldLabel(path)}
          </button>
        )}
      </>
    );
  }

  const label = fieldLabel(path);
  if (typeof value === 'boolean') {
    return (
      <Field label={label} path={path}>
        <input
          id={configFieldId(path)}
          data-config-path={path}
          type="checkbox"
          checked={value}
          onChange={(event) => onChange(path, event.target.checked)}
        />
      </Field>
    );
  }
  const options = enumOptionsFor(path);
  if (options) {
    const current = options.find((option) => option.value === String(value));
    return (
      <Field label={label} path={path}>
        <select
          id={configFieldId(path)}
          data-config-path={path}
          className="input"
          value={String(value)}
          onChange={(event) => onChange(path, event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {current && (
          <div style={{ color: 'var(--ink-2)', fontSize: 11, marginTop: 4, maxWidth: 460, lineHeight: 1.45 }}>
            {current.description}
          </div>
        )}
      </Field>
    );
  }
  return (
    <Field label={label} path={path}>
      <div style={{ display: 'flex', gap: 8 }}>
        {typeof value === 'number' ? (
          <NumericInput path={path} value={value} onChange={onChange} />
        ) : (
          <input
            id={configFieldId(path)}
            data-config-path={path}
            className="input"
            type="text"
            value={String(value)}
            onChange={(event) => onChange(path, event.target.value)}
          />
        )}
        {isNullablePath(path) && (
          <button className="btn" onClick={() => onChange(path, null)}>Clear</button>
        )}
      </div>
    </Field>
  );
}

function ArrayEditor({
  path,
  value,
}: {
  path: string;
  value: ConfigValue[];
}) {
  const arrayDraft = useContext(ArrayDraftContext);
  const text = arrayDraft?.values[path] ?? JSON.stringify(value, null, 2);
  const error = arrayDraft?.errors[path] ?? '';
  return (
    <Field label={fieldLabel(path)} path={path}>
      <textarea
        id={configFieldId(path)}
        data-config-path={path}
        className="input"
        rows={Math.min(12, Math.max(3, text.split('\n').length))}
        value={text}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${configFieldId(path)}-error` : undefined}
        onChange={(event) => arrayDraft?.onChange(path, event.target.value)}
      />
      {error && (
        <div id={`${configFieldId(path)}-error`} className="admin-config-field-error">
          {error}
        </div>
      )}
    </Field>
  );
}

function Field({ label, children, path }: { label: string; children: React.ReactNode; path?: string }) {
  const invalid = path !== undefined && useContext(InvalidPathsContext).has(path);
  return (
    <div className={invalid ? 'admin-config-field-row is-invalid' : 'admin-config-field-row'}>
      <label className="admin-config-field-label" htmlFor={path ? configFieldId(path) : undefined}>
        {label}
      </label>
      <div className="admin-config-field-control">
        {children}
        {path && <FieldHelp path={path} />}
      </div>
    </div>
  );
}

/// Guidance for the handful of settings whose humanized label does not say
/// enough — see FIELD_HELP. Renders nothing for everything else.
function FieldHelp({ path }: { path: string }) {
  const help = FIELD_HELP[path];
  const options = enumOptionsFor(path);
  if (!help && !options) return null;
  return (
    <div className="admin-config-field-help">
      {help}
      {options && (
        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
          {options.map((option) => (
            <li key={option.value}>
              {option.label === option.value
                ? <span className="mono">{option.value}</span>
                : <>{option.label} (<span className="mono">{option.value}</span>)</>}
              {' '}— {option.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptySection({ label, path, onEnable }: { label: string; path: string; onEnable: () => void }) {
  return (
    <div className="admin-config-empty-section">
      {label} is not configured.{' '}
      <button
        id={configFieldId(path)}
        data-config-path={path}
        className="btn"
        type="button"
        onClick={onEnable}
      >
        Configure
      </button>
    </div>
  );
}

function ValidationDetails({
  errors,
  onActivate,
}: {
  errors: ConfigValidationItem[];
  onActivate: (item: ConfigValidationItem) => void;
}) {
  if (errors.length === 0) return null;
  return (
    <section className="admin-config-validation-summary" aria-live="polite">
      <h3 className="admin-config-validation-title">
        Fix {errors.length} validation error{errors.length === 1 ? '' : 's'}
      </h3>
      <ul className="admin-config-validation-list">
        {errors.map((item) => {
          const location = configValidationLocation(item.path);
          return (
            <li key={`${item.path}:${item.message}`}>
              {location ? (
                <button
                  type="button"
                  className="admin-config-validation-link"
                  onClick={() => onActivate(item)}
                >
                  <span className="mono admin-config-validation-path">{item.path}</span>
                  <span>{item.message}</span>
                </button>
              ) : (
                <div className="admin-config-validation-unknown">
                  <span className="mono admin-config-validation-path">{item.path}</span>
                  <span>{item.message} · This backend path is not attached to a visible setting.</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function configValidationItems(error: unknown): ConfigValidationItem[] {
  if (!(error instanceof ApiError)) return [];
  const details = error.details as { errors?: ConfigValidationItem[] } | null;
  return details?.errors ?? [];
}

function configValidationLocation(path: string): {
  section: ConfigSectionDefinition;
  tab: ConfigTabDefinition;
} | null {
  const section = configValidationSection(path);
  if (!section) return null;
  if (path === section.key) return { section, tab: section.tabs[0]! };
  const tab = tabForConfigPath(section, path);
  return tab ? { section, tab } : null;
}

function configValidationSection(path: string): ConfigSectionDefinition | null {
  return CONFIG_SECTIONS.find((candidate) => (
    path === candidate.key || path.startsWith(`${candidate.key}.`)
  )) ?? null;
}

function ConfigStatusBar({
  status,
  desired,
  applied,
  error,
  nodes,
  conflict,
  baseVersion,
  onRetry,
  retrying,
}: {
  status: string;
  desired: number | null;
  applied: number | null;
  error: string | null;
  nodes: ConfigWireNode[];
  conflict: boolean;
  baseVersion: number | null;
  onRetry: () => void;
  retrying: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  useOutsideClick(wrapRef, () => setOpen(false));
  const nodeIsConverged = (node: ConfigWireNode) => (
    desired !== null
    && node.desired_version === desired
    && node.applied_version === desired
    && node.status === 'applied'
    && !node.stale
  );
  const behind = nodes.filter((node) => !nodeIsConverged(node));
  const colorClass = conflict || status === 'failed'
    ? 'is-bad'
    : status === 'applied' && nodes.length > 0 && behind.length === 0 ? 'is-good' : 'is-warn';
  return (
    <div className={`admin-config-status ${colorClass}`}>
      <div className="admin-config-status-main" role="status" aria-live="polite">
        <span className={`mono admin-config-status-state ${colorClass}`}>
          {status}
        </span>
        <span className="admin-config-status-summary">
          Desired v{desired ?? '—'} · Applied v{applied ?? '—'}
        </span>
        {conflict && (
          <span className="admin-config-status-conflict">
            Conflict · draft based on v{baseVersion ?? '—'}
          </span>
        )}
        {error && <span className="admin-config-status-error" title={error}>{error}</span>}
      </div>
      <div
        className="admin-config-status-actions"
        ref={wrapRef}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !open) return;
          event.preventDefault();
          setOpen(false);
          window.requestAnimationFrame(() => triggerRef.current?.focus());
        }}
      >
        {status === 'failed' && (
          <button className="btn btn-sm" type="button" disabled={retrying} onClick={onRetry}>
            {retrying ? 'Retrying…' : 'Retry apply'}
          </button>
        )}
        {nodes.length > 0 && (
          <button
            ref={triggerRef}
            className="btn btn-sm admin-config-status-trigger"
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            aria-controls="admin-config-node-status"
            onClick={() => setOpen((current) => !current)}
          >
            Nodes {nodes.length - behind.length}/{nodes.length}
          </button>
        )}
        {nodes.length === 0 && (
          <span className="admin-config-status-count" aria-label="No node convergence reports available">
            Nodes 0/0
          </span>
        )}
        {open && (
          <div id="admin-config-node-status" className="admin-config-status-popover" role="region" aria-label="Node convergence details">
            <div className="admin-config-status-popover-head">Node convergence</div>
            {nodes.map((node) => {
              const converged = nodeIsConverged(node);
              const nodeClass = node.stale || node.status === 'failed'
                ? 'is-bad'
                : converged ? 'is-good' : 'is-warn';
              return (
                <div key={node.node_id} className={`admin-config-status-node ${nodeClass}`}>
                  <span className="admin-config-status-node-name">● {node.node_id}</span>
                  <span className="admin-config-status-node-version">
                    {node.stale ? 'Not reporting' : `v${node.applied_version ?? '—'} · ${node.status}`}
                  </span>
                  <span className="admin-config-status-node-detail">
                    {node.last_error ?? `Last reported ${fmtStampEt(node.updated_at)}`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface ConfigSearchEntry {
  path: string;
  label: string;
  section: string;
  sectionLabel: string;
  tab: string;
  tabLabel: string;
  searchText: string;
}

function ConfigSettingSearch({
  entries,
  onSelect,
}: {
  entries: ConfigSearchEntry[];
  onSelect: (entry: ConfigSearchEntry) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useOutsideClick(wrapperRef, () => setOpen(false));
  const needle = query.trim().toLocaleLowerCase();
  const allMatches = needle
    ? entries.filter((entry) => entry.searchText.includes(needle))
    : [];
  const matches = allMatches.slice(0, 8);
  const safeActiveIndex = Math.min(activeIndex, Math.max(0, matches.length - 1));
  const listboxId = 'admin-config-setting-results';

  useEffect(() => setActiveIndex(0), [needle]);

  const select = (entry: ConfigSearchEntry) => {
    onSelect(entry);
    setQuery('');
    setOpen(false);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div
      className="admin-config-search"
      ref={wrapperRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <div className="admin-config-search-control">
        <span aria-hidden="true" className="admin-config-search-icon">{Icons.search}</span>
        <input
          ref={inputRef}
          className="admin-config-search-input"
          type="search"
          role="combobox"
          aria-label="Find a configuration setting"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open && needle.length > 0}
          aria-controls={listboxId}
          aria-activedescendant={open && matches[safeActiveIndex] ? `config-search-${safeActiveIndex}` : undefined}
          placeholder="Find setting…"
          value={query}
          onFocus={() => { if (needle) setOpen(true); }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(event.target.value.trim().length > 0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (matches.length === 0) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              const delta = event.key === 'ArrowDown' ? 1 : -1;
              setActiveIndex((current) => (current + delta + matches.length) % matches.length);
            }
            if (event.key === 'Enter' && open) {
              event.preventDefault();
              select(matches[safeActiveIndex]!);
            }
          }}
        />
        {query && (
          <button
            type="button"
            className="admin-config-search-clear"
            aria-label="Clear setting search"
            onClick={() => {
              setQuery('');
              setOpen(false);
              inputRef.current?.focus();
            }}
          >
            {Icons.x}
          </button>
        )}
      </div>
      {open && needle && (
        <div className="admin-config-search-results" id={listboxId} role="listbox">
          {matches.length === 0 ? (
            <div className="admin-config-search-empty">No matching settings.</div>
          ) : (
            <>
              {matches.map((entry, index) => (
                <button
                  key={entry.path}
                  id={`config-search-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === safeActiveIndex}
                  className={`admin-config-search-option ${index === safeActiveIndex ? 'on' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(entry)}
                >
                  <span>{entry.sectionLabel} › {entry.tabLabel} › {entry.label}</span>
                  <span className="mono admin-config-search-path">{entry.path}</span>
                </button>
              ))}
              {allMatches.length > matches.length && (
                <div className="admin-config-search-more">
                  {allMatches.length - matches.length} more matches · refine your search
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function buildConfigSearchEntries(document: ConfigDocument): ConfigSearchEntry[] {
  const entries: ConfigSearchEntry[] = [];
  for (const section of CONFIG_SECTIONS) {
    const rawSection = document[section.key];
    const sectionValue = rawSection ?? OPTIONAL_DEFAULTS[section.key];
    const sectionObject = isConfigObject(sectionValue) ? sectionValue : {};
    for (const candidateTab of section.tabs) {
      for (const key of candidateTab.fieldKeys) {
        const basePath = `${section.key}.${key}`;
        const configuredValue = sectionObject[key];
        const fallbackValue = configuredValue === null
          ? OPTIONAL_DEFAULTS[basePath]
          : undefined;
        collectConfigSearchEntries(
          entries,
          section,
          candidateTab,
          basePath,
          fallbackValue ?? configuredValue,
        );
      }
      for (const path of candidateTab.secretPaths) {
        entries.push(configSearchEntry(section, candidateTab, path));
      }
    }
  }
  return entries;
}

function collectConfigSearchEntries(
  entries: ConfigSearchEntry[],
  section: ConfigSectionDefinition,
  tab: ConfigTabDefinition,
  path: string,
  value: ConfigValue | undefined,
) {
  if (isConfigObject(value)) {
    const children = Object.entries(value);
    if (children.length > 0) {
      for (const [key, child] of children) {
        collectConfigSearchEntries(entries, section, tab, `${path}.${key}`, child);
      }
      return;
    }
  }
  entries.push(configSearchEntry(section, tab, path));
}

function configSearchEntry(
  section: ConfigSectionDefinition,
  tab: ConfigTabDefinition,
  path: string,
): ConfigSearchEntry {
  const label = fieldLabel(path);
  return {
    path,
    label,
    section: section.key,
    sectionLabel: section.label,
    tab: tab.id,
    tabLabel: tab.label,
    searchText: [
      section.label,
      tab.label,
      label,
      path,
      FIELD_HELP[path] ?? '',
      ...(enumOptionsFor(path) ?? []).flatMap((option) => [
        option.value,
        option.label,
        option.description,
      ]),
    ].join(' ').toLocaleLowerCase(),
  };
}

function configFieldId(path: string) {
  return `admin-config-field-${path.replaceAll('.', '-')}`;
}

function findConfigFieldElement(path: string): HTMLElement | null {
  const segments = path.split('.');
  while (segments.length > 0) {
    const match = document.getElementById(configFieldId(segments.join('.')));
    if (match) return match;
    segments.pop();
  }
  return null;
}

function isConfigObject(value: ConfigValue | undefined): value is Record<string, ConfigValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function configValuesEqual(left: ConfigValue | ConfigDocument | undefined, right: ConfigValue | ConfigDocument | undefined) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function ApplyModal({ status, desired, onDismiss }: {
  status: string; desired: number | null; onDismiss: () => void;
}) {
  return (
    <ResponsiveOverlay label="Applying configuration" kind="progress" dismissible={false} onClose={onDismiss} className="config-progress">
      <div style={{ width: 420, background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10, padding: 26, boxShadow: '0 20px 60px rgba(0,0,0,.22)' }}>
        <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 10 }}>CONFIG VERSION {desired ?? '…'}</div>
        <h2 style={{ margin: '8px 0' }}>Applying configuration</h2>
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          {status === 'pending' && 'SAM Server notified; waiting to prepare the new generation…'}
          {status === 'preparing' && 'Validating clients, schedules, channels, and privacy bounds…'}
          {status === 'applying' && 'Publishing the new runtime generation atomically…'}
          {status === 'applied' && 'Configuration applied.'}
        </div>
        <div style={{ height: 4, background: 'var(--line)', marginTop: 20, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: status === 'applying' ? '82%' : status === 'preparing' ? '55%' : '25%', background: 'var(--accent)', transition: 'width .25s' }} />
        </div>
        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>
            The previous configuration keeps running until this succeeds. An apply
            SAM Server stops reporting on is marked failed after 10 minutes.
          </span>
          <button className="btn" onClick={onDismiss}>Run in background</button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

function setAtPath(root: JsonObject, segments: string[], value: ConfigValue) {
  let current: JsonObject = root;
  for (let index = 0; index < segments.length - 1; index += 1) {
    const next = current[segments[index]!];
    if (!next || typeof next !== 'object' || Array.isArray(next)) throw new Error(`Invalid config path ${segments.join('.')}`);
    current = next as JsonObject;
  }
  current[segments.at(-1)!] = value;
}

/// Labels `humanize()` would get wrong (an acronym), keyed by dotted path.
const FIELD_LABELS: Record<string, string> = {
  'llm.cache_ttl': 'Prompt cache TTL',
  'edge.api_keys_enabled': 'API keys enabled',
  'edge.mcp_enabled': 'Connector for Claude and ChatGPT (MCP) enabled',
  'edge.credential_prepare_enabled': 'Credentials may prepare launches',
  'edge.credential_admin_enabled': 'Credentials may administer',
};

function fieldLabel(path: string) {
  return FIELD_LABELS[path] ?? humanize(path.split('.').at(-1)!);
}

function humanize(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
